"use client";

import { useRef } from "react";
import Link from "next/link";
import { motion, useInView } from "framer-motion";
import { Code2, ArrowRight, Github, Twitter, Linkedin } from "lucide-react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const footerLinks = [
  { icon: Twitter, label: "Twitter", href: "#" },
  { icon: Github, label: "Github", href: "#" },
  { icon: Linkedin, label: "LinkedIn", href: "#" },
];

export function CTASection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });
  const reducedMotion = useReducedMotion();

  return (
    <section
      ref={ref}
      id="cta"
      className="relative min-h-screen w-full flex flex-col overflow-hidden"
    >
      {/* Epic gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#0f0f23]" />

        {/* Animated gradient orbs */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-[800px] h-[800px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(124,58,237,0.3) 0%, transparent 60%)",
            filter: "blur(80px)",
          }}
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(6,182,212,0.3) 0%, transparent 60%)",
            filter: "blur(80px)",
          }}
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
            scale: [1.2, 1, 1.2],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(236,72,153,0.25) 0%, transparent 60%)",
            filter: "blur(100px)",
          }}
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.3,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 4 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      {/* Main CTA Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center py-32">
        <div className="container mx-auto px-6 text-center">
          {/* Animated logo */}
          <motion.div
            className="mb-10"
            initial={{ scale: 0, rotate: -180 }}
            animate={isInView ? { scale: 1, rotate: 0 } : {}}
            transition={{
              duration: reducedMotion ? 0 : 1,
              type: "spring",
              stiffness: 100,
            }}
          >
            <div className="relative inline-flex">
              {/* Pulsing glow */}
              <motion.div
                className="absolute inset-0 rounded-3xl"
                style={{
                  background: "linear-gradient(135deg, #7c3aed, #06b6d4, #ec4899)",
                  filter: "blur(30px)",
                }}
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.4, 0.7, 0.4],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <div className="relative w-24 h-24 rounded-3xl bg-gradient-to-br from-violet-600 via-purple-600 to-cyan-500 p-[2px]">
                <div className="w-full h-full rounded-3xl bg-[#0f0f23] flex items-center justify-center">
                  <Code2 className="w-12 h-12 text-white" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Main headline */}
          <motion.h2
            className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <span className="block text-white">START BUILDING</span>
            <span className="block bg-gradient-to-r from-violet-400 via-cyan-400 to-pink-400 bg-clip-text text-transparent">
              TODAY
            </span>
          </motion.h2>

          {/* Subtitle */}
          <motion.p
            className="max-w-xl mx-auto text-xl text-white/60 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Join thousands of developers building the future with Vibe.
            No credit card required.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <Link href="/auth/register">
              <motion.button
                className="group relative px-10 py-5 bg-gradient-to-r from-violet-600 via-purple-600 to-cyan-600 rounded-2xl text-lg font-bold text-white overflow-hidden shadow-2xl shadow-violet-500/30"
                whileHover={{ scale: 1.05, boxShadow: "0 30px 60px rgba(124, 58, 237, 0.5)" }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 flex items-center gap-3">
                  GET STARTED FREE
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="w-5 h-5" />
                  </motion.span>
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-violet-600"
                  initial={{ x: "100%" }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            </Link>

            <Link href="/auth/login">
              <motion.button
                className="px-10 py-5 border border-white/20 rounded-2xl text-lg font-medium text-white/80 hover:bg-white/10 hover:border-white/40 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                SIGN IN
              </motion.button>
            </Link>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            className="flex flex-wrap justify-center gap-6"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            {["No credit card", "Free tier forever", "Cancel anytime"].map(
              (badge) => (
                <div key={badge} className="flex items-center gap-2 text-white/50">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-sm">{badge}</span>
                </div>
              )
            )}
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-8 bg-[#0f0f23]/80 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <motion.div
            className="flex flex-col md:flex-row justify-between items-center gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1, duration: 0.6 }}
          >
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
                <Code2 className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold tracking-wider text-white/80">VIBE</span>
            </div>

            {/* Copyright */}
            <p className="text-sm text-white/40">
              &copy; 2026 intrazero.com
            </p>

            {/* Social links */}
            <div className="flex gap-4">
              {footerLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all"
                  aria-label={link.label}
                >
                  <link.icon className="w-5 h-5" />
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </footer>
    </section>
  );
}

export default CTASection;
